import React from "react";
import {
  Box,
  Button,
  Heading,
  Stack,
  Text,
  Avatar,
  usePrefersReducedMotion,
} from "@chakra-ui/react";
  import keyholeGif from '../../assets/gif/keyhole.gif';


export type BlurOverlayProps = {
  isActive: boolean;
  title?: string;
  description?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  blur?: number;
  dim?: number;
  showLockIcon?: boolean;
  overlayContent?: JSX.Element;
  zIndex?: number;
  clipRadius?: string | number;
};

function DefaultCard({
  title,
  description,
  ctaLabel,
  onCtaClick,
  showLockIcon,
}: {
  title?: string;
  description?: string;
  ctaLabel?: string;
  onCtaClick?: () => void;
  showLockIcon?: boolean;
}) {
  return (
    <Box
      bg="white"
      _dark={{ bg: "gray.800" }}
      rounded="2xl"
      p={6}
      shadow="xl"
      minW={{ base: "xs", md: "sm" }}
      textAlign="center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="blur-overlay-title"
    >
      <Stack spacing={4} align="center">
        {showLockIcon && <Avatar src={keyholeGif} size="lg"/>}
        <Heading id="blur-overlay-title" size="md">
          {title}
        </Heading>
        {description && <Text opacity={0.9}>{description}</Text>}
        {ctaLabel && (
          <Button size="md" onClick={onCtaClick}>
            {ctaLabel}
          </Button>
        )}
      </Stack>
    </Box>
  );
}

export function BlurOverlay({
  isActive,
  title = "Please Log In To View This",
  description,
  ctaLabel,
  onCtaClick,
  blur = 10,
  dim = 0.35,
  showLockIcon = true,
  overlayContent,
  zIndex = 1000,
  clipRadius = 16,
}: BlurOverlayProps) {
  const prefersReducedMotion = usePrefersReducedMotion();

  return (
    <Box
      aria-hidden={!isActive}
      pointerEvents={isActive ? "auto" : "none"}
      position="absolute"
      inset={0}
      display={isActive ? "block" : "none"}
      // Local blur plane; DO NOT use overflow here (breaks sticky). Clip with clip-path instead.
      sx={{
        backdropFilter: isActive ? `blur(${blur}px)` : undefined,
        WebkitBackdropFilter: isActive ? `blur(${blur}px)` : undefined,
        clipPath: `inset(0 round ${typeof clipRadius === "number" ? `${clipRadius}px` : clipRadius})`,
        // If you previously set transform on ancestors, sticky can break. Avoid transforms on outer wrappers.
      }}
      bg={`rgba(0,0,0,${dim})`}
      zIndex={zIndex}
      transition={prefersReducedMotion ? undefined : "opacity 160ms ease"}
    >
      {/* Sticky viewport frame INSIDE the blur plane */}
      <Box
        position="sticky"
        top={20}
        h={{base: '60vh', md: '40vh'}}           // full viewport height so card stays centered while the PAGE scrolls
        display="flex"
        alignItems="center"
        justifyContent="center"
        p={4}
      >
        {overlayContent ? (
          <Box>{overlayContent}</Box>
        ) : (
          <DefaultCard
            title={title}
            description={description}
            ctaLabel={ctaLabel}
            onCtaClick={onCtaClick}
            showLockIcon={showLockIcon}
          />
        )}
      </Box>
    </Box>
  );
}

export type BlurGuardProps = Omit<BlurOverlayProps, "zIndex"> & {
  children: React.ReactNode;
  blockInteractions?: boolean;
  /** z-index for overlay layers */
  zIndex?: number;
};

export default function BlurGuard({
  children,
  isActive,
  blockInteractions = true,
  blur,
  dim,
  title,
  description,
  ctaLabel,
  onCtaClick,
  showLockIcon,
  overlayContent,
  clipRadius = 16,
  zIndex = 1000,
}: BlurGuardProps) {
  return (
    // IMPORTANT: overflow must be VISIBLE so sticky can work.
    <Box position="relative">
      <Box
        aria-hidden={isActive ? true : undefined}
        pointerEvents={isActive && blockInteractions ? "none" : "auto"}
        userSelect={isActive ? "none" : "auto"}
      >
        {children}
      </Box>

      <BlurOverlay
        isActive={isActive}
        blur={blur}
        dim={dim}
        title={title}
        description={description}
        ctaLabel={ctaLabel}
        onCtaClick={onCtaClick}
        showLockIcon={showLockIcon}
        overlayContent={overlayContent}
        clipRadius={clipRadius}
        zIndex={zIndex}
      />
    </Box>
  );
}
